import { Sidebar } from '@components/Sidebar'
import { SidebarDrawer } from '@components/SidebarDrawer'
import type { AppTheme } from '@components/Theme'
import useAppState from '@hooks/useAppState'
import useNavigator from '@hooks/useNavigator'
import { useMediaQuery } from '@mui/material'
import { useReminderDueFeed } from '@sections/cases/hooks/useReminderDueFeed'
import { useGlobalUnreadFeed } from '@sections/messaging/hooks/useGlobalUnreadFeed'
import { Container } from '@ui/Container'
import ErrorPage from '@ui/ErrorPage'
import type React from 'react'
import { useContext } from 'react'

import { AppLayoutContext, useAppLayoutActionAnchor } from './AppLayoutContext'
import { useAppLayoutHeaderAnchor } from './AppLayoutContext'
import { SidebarLink } from './SidebarLink'
import * as S from './styles'
import useMenuStructure from './useMenuStructure'

interface AppLayoutProps {
  children: React.ReactNode
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { setAnchor: setAppActionAnchor } = useAppLayoutActionAnchor()
  const { setAnchor: setAppHeaderAnchor } = useAppLayoutHeaderAnchor()
  const navigate = useNavigator()
  const [appState, dispatchAppState] = useAppState()

  // TODO do this with CSS?
  const isMdUp = useMediaQuery((theme: AppTheme) => theme.breakpoints.up('md'))

  const appLayoutContext = useContext(AppLayoutContext)
  const contentBackgroundType = appLayoutContext?.background?.current ?? 'default'
  const isHeaderHidden = appLayoutContext?.headerHidden?.current ?? false

  const menuStructure = useMenuStructure()

  // App-wide unread feed: nav badge, title count and browser notifications
  // stay live on every authenticated page, not only in the messaging inbox.
  useGlobalUnreadFeed()
  // Reminder-due toasts and OS notifications, on every page like the above.
  useReminderDueFeed()

  const handleErrorBack = () => {
    navigate('/')
    dispatchAppState({
      payload: {
        error: null,
      },
      type: 'displayError',
    })
  }

  return (
    <S.Root>
      {isMdUp ? (
        <Sidebar menuItems={menuStructure} logoHref="/" linkComponent={SidebarLink} />
      ) : null}
      <S.Content background={contentBackgroundType}>
        {appState.loading ? <S.AppLoader color="primary" /> : null}
        <S.ViewContainer>
          <S.HeaderWrapper hidden={isHeaderHidden}>
            <Container>
              <S.Header className="app-header-bar" ref={setAppHeaderAnchor}>
                {!isMdUp ? (
                  <SidebarDrawer
                    menuItems={menuStructure}
                    logoHref="/"
                    linkComponent={SidebarLink}
                  />
                ) : null}
              </S.Header>
            </Container>
          </S.HeaderWrapper>
          <S.MainView>
            {appState.error
              ? appState.error === 'unhandled' && (
                  <ErrorPage
                    minHeight="calc(100vh - 12rem)"
                    onBack={handleErrorBack}
                    onRefresh={() => window.location.reload()}
                  />
                )
              : children}
          </S.MainView>
        </S.ViewContainer>
        <S.AppAction ref={setAppActionAnchor} />
      </S.Content>
    </S.Root>
  )
}

export default AppLayout
